import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Hash, List, CheckSquare, Calendar, Type } from 'lucide-react';

const renderCustomFieldInput = (field, value, onChange, isRequired = false) => {
  const handleChange = (newValue) => {
    onChange(newValue);
  };
  
  switch (field.field_type) {
    case 'select':
      return (
        <Select 
          value={value || ''} 
          onValueChange={handleChange}
          required={isRequired}
        >
          <SelectTrigger>
            <SelectValue placeholder={`Sélectionner ${field.name}`} />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((option, idx) => (
              <SelectItem key={`${field.id}-opt-${idx}`} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'checkbox':
      return (
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => handleChange(e.target.checked)}
            className="h-4 w-4"
          />
          <span className="text-sm text-gray-600">Oui</span>
        </div>
      );
    case 'date':
      return (
        <Input
          type="date"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          required={isRequired}
        />
      );
    case 'number':
      return (
        <Input
          type="number"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          required={isRequired}
        />
      );
    default:
      return (
        <Input
          type="text"
          value={value || ''}
          onChange={(e) => handleChange(e.target.value)}
          required={isRequired}
        />
      );
  }
};

const PatientFormDialog = ({
  isOpen,
  onOpenChange,
  patient,
  setPatient,
  groupes,
  customFields,
  onSubmit,
  isEditing = false,
  patientLabel,
  triggerButton
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      {triggerButton && <DialogTrigger asChild>{triggerButton}</DialogTrigger>}
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? `Modifier le ${patientLabel}` : `Ajouter un ${patientLabel}`}</DialogTitle>
          <DialogDescription>
            {isEditing ? `Modifiez les informations du ${patientLabel}` : `Renseignez les informations du ${patientLabel}`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          {/* Fixed fields: Nom and Prénom */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="patient-prenom">Prénom *</Label>
              <Input
                id="patient-prenom"
                value={patient.prenom}
                onChange={(e) => setPatient({...patient, prenom: e.target.value})}
                required
                data-testid="patient-firstname-input"
              />
            </div>
            <div>
              <Label htmlFor="patient-nom">Nom *</Label>
              <Input
                id="patient-nom"
                value={patient.nom}
                onChange={(e) => setPatient({...patient, nom: e.target.value})}
                required
                data-testid="patient-name-input"
              />
            </div>
          </div>

          {/* Dynamic custom fields */}
          {customFields.map((field) => (
            <div key={field.id}>
              <Label htmlFor={`patient-${field.field_key}`}>
                {field.name} {field.is_required && '*'}
              </Label>
              {renderCustomFieldInput(
                field,
                patient.custom_fields?.[field.field_key],
                (value) => setPatient({
                  ...patient,
                  custom_fields: {
                    ...patient.custom_fields,
                    [field.field_key]: value
                  }
                }),
                field.is_required
              )}
            </div>
          ))}

          {/* Groupe selection */}
          <div>
            <Label htmlFor="patient-groupe">Groupe</Label>
            <Select 
              value={patient.groupe_id || 'none'} 
              onValueChange={(value) => setPatient({...patient, groupe_id: value === 'none' ? '' : value})}
            >
              <SelectTrigger data-testid="patient-group-select">
                <SelectValue placeholder="Sélectionner un groupe" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun groupe</SelectItem>
                {groupes.map(groupe => (
                  <SelectItem key={groupe.id} value={groupe.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: groupe.couleur }}
                      />
                      {groupe.nom}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" className="w-full" data-testid="submit-patient-btn">
            {isEditing ? `Modifier le ${patientLabel}` : `Créer le ${patientLabel}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export { PatientFormDialog, renderCustomFieldInput };
